import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  BugReport as BugIcon,
  CheckCircle as PassedIcon,
  Cancel as FailedIcon,
  Schedule as PendingIcon,
  PlayArrow as RunIcon,
  Stop as StopIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Assignment as TestIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Speed as PerformanceIcon,
  Devices as DeviceIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assessment as ReportIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import QATestingService, { TestCase, BugReport, TestStep } from '../../services/QATestingService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`qa-tabpanel-${index}`}
      aria-labelledby={`qa-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * QA Testing Component for Project Manager
 * Manages test cases, bug reports, test execution, and quality assurance metrics
 */
const QATesting: React.FC = () => {
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [bugReports, setBugReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<TestCase | BugReport | null>(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [bugDialogOpen, setBugDialogOpen] = useState(false);
  const [runningTests, setRunningTests] = useState<Set<number>>(new Set());

  const projects = ['E-commerce Platform Redesign', 'Mobile App Development', 'Dashboard Analytics Implementation'];

  useEffect(() => {
    loadQAData();
  }, []);

  const loadQAData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Load test cases from API
      const testCasesData = await QATestingService.getTestCases({
        type: filterType !== 'all' ? filterType as any : undefined,
        status: filterStatus !== 'all' ? filterStatus as any : undefined,
      });
      
      // Load bug reports from API
      const bugReportsData = await QATestingService.getBugReports();
      
      setTestCases(testCasesData);
      setBugReports(bugReportsData);
    } catch (err) {
      setError('Failed to load QA data. Please try again.');
      console.error('Error loading QA data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: TestCase | BugReport) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleRunTest = async (testId: number) => {
    setRunningTests(prev => new Set(Array.from(prev).concat(testId)));
    
    try {
      // Simulate test execution duration
      const duration = Math.floor(Math.random() * 60) + 30; // 30-90 seconds
      const status = Math.random() > 0.3 ? 'passed' : 'failed';
      
      await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3s for simulation
      
      // Execute test via API
      const result = await QATestingService.executeTest(testId, {
        status,
        duration_seconds: duration,
      });
      
      // Update test case in local state
      setTestCases(prev => prev.map(test => 
        test.id === testId ? result.test_case : test
      ));
    } catch (err) {
      setError('Failed to execute test. Please try again.');
      console.error('Error executing test:', err);
    } finally {
      setRunningTests(prev => {
        const newSet = new Set(prev);
        newSet.delete(testId);
        return newSet;
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed': return <PassedIcon color="success" />;
      case 'failed': return <FailedIcon color="error" />;
      case 'running': return <CircularProgress size={20} />;
      case 'pending': return <PendingIcon color="warning" />;
      default: return <PendingIcon />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'unit': return <CodeIcon />;
      case 'integration': return <TestIcon />;
      case 'performance': return <PerformanceIcon />;
      case 'security': return <SecurityIcon />;
      case 'e2e': return <DeviceIcon />;
      default: return <TestIcon />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getTestStats = () => {
    const total = testCases.length;
    const passed = testCases.filter(t => t.status === 'passed').length;
    const failed = testCases.filter(t => t.status === 'failed').length;
    const pending = testCases.filter(t => t.status === 'pending').length;
    const running = testCases.filter(t => t.status === 'running').length;
    
    return { total, passed, failed, pending, running };
  };

  const getBugStats = () => {
    const total = bugReports.length;
    const open = bugReports.filter(b => b.status === 'open').length;
    const inProgress = bugReports.filter(b => b.status === 'in-progress').length;
    const resolved = bugReports.filter(b => b.status === 'resolved').length;
    
    return { total, open, inProgress, resolved };
  };

  const stats = getTestStats();
  const bugStats = getBugStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          QA & Testing
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<BugIcon />}
            onClick={() => setBugDialogOpen(true)}
          >
            Report Bug
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setTestDialogOpen(true)}
          >
            New Test Case
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={loadQAData}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      )}

      {/* Stats Overview */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 2,
          mb: 3
        }}
      >
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Tests
            </Typography>
            <Typography variant="h4">{stats.total}</Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip label={`${stats.passed} Passed`} size="small" color="success" />
              <Chip label={`${stats.failed} Failed`} size="small" color="error" />
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Test Coverage
            </Typography>
            <Typography variant="h4">85%</Typography>
            <LinearProgress 
              variant="determinate" 
              value={85} 
              color="success"
              sx={{ mt: 1, height: 8, borderRadius: 4 }}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Open Bugs
            </Typography>
            <Typography variant="h4">{bugStats.open + bugStats.inProgress}</Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip label={`${bugStats.open} Open`} size="small" color="error" />
              <Chip label={`${bugStats.inProgress} In Progress`} size="small" color="warning" />
            </Box>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Success Rate
            </Typography>
            <Typography variant="h4">
              {stats.total > 0 ? Math.round((stats.passed / stats.total) * 100) : 0}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last 30 days
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="qa tabs">
            <Tab label="Test Cases" />
            <Tab label="Bug Reports" />
            <Tab label="Test Execution" />
            <Tab label="Reports" />
          </Tabs>
        </Box>

        {/* Test Cases Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box display="flex" gap={2} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                label="Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="unit">Unit</MenuItem>
                <MenuItem value="integration">Integration</MenuItem>
                <MenuItem value="e2e">E2E</MenuItem>
                <MenuItem value="performance">Performance</MenuItem>
                <MenuItem value="security">Security</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="passed">Passed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="running">Running</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<RunIcon />}
              onClick={() => testCases.forEach(test => handleRunTest(test.id))}
            >
              Run All Tests
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Test Case</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testCases.map((test) => (
                  <TableRow key={test.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {test.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {test.description}
                        </Typography>
                        {test.tags && test.tags.length > 0 && (
                          <Box display="flex" gap={0.5} mt={0.5}>
                            {test.tags.map(tag => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getTypeIcon(test.type)}
                        {test.type}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {runningTests.has(test.id) ? (
                          <CircularProgress size={16} />
                        ) : (
                          getStatusIcon(test.status)
                        )}
                        <Chip 
                          label={runningTests.has(test.id) ? 'running' : test.status} 
                          size="small"
                          color={
                            runningTests.has(test.id) ? 'info' :
                            test.status === 'passed' ? 'success' :
                            test.status === 'failed' ? 'error' : 'default'
                          }
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={test.priority} 
                        size="small"
                        color={test.priority === 'high' ? 'error' : test.priority === 'medium' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {test.assignee_name ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                            {test.assignee_name.split(' ').map((n: string) => n[0]).join('')}
                          </Avatar>
                          {test.assignee_name}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                      )}
                    </TableCell>
                    <TableCell>{test.duration_seconds ? formatDuration(test.duration_seconds) : '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleRunTest(test.id)}
                        disabled={runningTests.has(test.id)}
                      >
                        <RunIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, test)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Bug Reports Tab */}
        <TabPanel value={activeTab} index={1}>
          <List>
            {bugReports.map((bug, index) => (
              <React.Fragment key={bug.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BugIcon color="error" />
                        <Typography variant="h6">{bug.title}</Typography>
                        <Chip 
                          label={bug.severity} 
                          size="small"
                          color={getSeverityColor(bug.severity) as any}
                        />
                        <Chip 
                          label={bug.status} 
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {bug.description}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          <Typography variant="caption">
                            <strong>Reported by:</strong> {bug.reporter_name}
                          </Typography>
                          <Typography variant="caption">
                            <strong>Assigned to:</strong> {bug.assignee_name || 'Unassigned'}
                          </Typography>
                          <Typography variant="caption">
                            <strong>Project:</strong> {bug.project?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption">
                            <strong>Version:</strong> {bug.found_in_version || 'N/A'}
                          </Typography>
                        </Box>
                        {bug.attachments && bug.attachments.length > 0 && (
                          <Box mt={1}>
                            <Typography variant="caption" color="text.secondary">
                              Attachments: {bug.attachments.join(', ')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={(e) => handleMenuOpen(e, bug)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < bugReports.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        {/* Test Execution Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box textAlign="center" py={4}>
            <RunIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Test Execution Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Real-time test execution monitoring will be displayed here.
            </Typography>
          </Box>
        </TabPanel>

        {/* Reports Tab */}
        <TabPanel value={activeTab} index={3}>
          <Box textAlign="center" py={4}>
            <ReportIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              QA Reports
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed testing reports and analytics will be available here.
            </Typography>
            <Button variant="contained" sx={{ mt: 2 }} startIcon={<ReportIcon />}>
              Generate Report
            </Button>
          </Box>
        </TabPanel>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <RunIcon sx={{ mr: 1 }} fontSize="small" />
          Run Test
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Test Case Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Test Case</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField label="Test Title" fullWidth />
            <TextField label="Description" multiline rows={3} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select defaultValue="integration" label="Type">
                <MenuItem value="unit">Unit Test</MenuItem>
                <MenuItem value="integration">Integration Test</MenuItem>
                <MenuItem value="e2e">End-to-End Test</MenuItem>
                <MenuItem value="performance">Performance Test</MenuItem>
                <MenuItem value="security">Security Test</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select defaultValue="medium" label="Priority">
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Create Test Case</Button>
        </DialogActions>
      </Dialog>

      {/* Bug Report Dialog */}
      <Dialog open={bugDialogOpen} onClose={() => setBugDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Report New Bug</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField label="Bug Title" fullWidth />
            <TextField label="Description" multiline rows={4} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Severity</InputLabel>
              <Select defaultValue="medium" label="Severity">
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Steps to Reproduce" multiline rows={3} fullWidth />
            <TextField label="Version Found In" fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBugDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error">Report Bug</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QATesting;